import { CartItemTypeEnum, type CartItem } from '../models/cart.model';
import type {
    CartItemInterface,
    CartItemPrices,
    ConfigurableCartItem,
    ConfigurableProduct,
    ConfigurableVariant,
    CartPrices,
    Money,
    Discount,
    CurrencyEnum,
    Maybe,
} from '../types/front.graphql';
import type { CartItemReference } from '../models';
import { type AddToCartProps } from '../components/add-to-cart';
import { type ItemByProductIdWithoutGiftsInCart$ } from '../components/add-to-cart/add-to-cart.types';
import { getItemEffectiveUnitPrice } from '../helpers/cart-item.helpers';

export type UpdateItemInput = Partial<Omit<CartItemInterface, 'id'>>;

/**
 * @deprecated Use `updateCartItemQuantityAndPrices` instead.
 * Ratio-based approach is unreliable with stale row_total data.
 */
export const calculateCartItemRowTotal = (value: number, currentQuantity: number, newQuantity: number): number => {
    if (currentQuantity === 0) {
        return value * newQuantity;
    }
    return (value / currentQuantity) * newQuantity;
};

/**
 * Update cart item quantity and recalculate prices (row_total, row_total_including_tax).
 *
 * Uses unit-price-based derivation (getItemEffectiveUnitPrice) instead of
 * ratio-based row_total/oldQty*newQty to avoid stale data and rounding errors.
 *
 * Strips stale item-level discounts — accurate discounts arrive via FastCartTotals.
 *
 * CRITICAL: Must be called whenever quantity changes to keep prices in sync.
 */
export const updateCartItemQuantityAndPrices = (
    item: CartItemInterface,
    updatedQuantity: number
): CartItemInterface => {
    const unitPrice = getItemEffectiveUnitPrice(item);
    const newRowTotal = unitPrice * updatedQuantity;

    const rowTotal = item?.prices?.row_total;
    const rowTotalIncludingTax = item?.prices?.row_total_including_tax;

    const updatedPrices: CartItemPrices = {
        ...(item?.prices as CartItemPrices),
        price: {
            ...item?.prices?.price,
            value: unitPrice,
        } as Money,
        row_total: {
            ...rowTotal,
            value: newRowTotal,
        } as Money,
        row_total_including_tax: {
            ...rowTotalIncludingTax,
            value: newRowTotal,
        } as Money,
        discounts: null,
        total_item_discount: { __typename: 'Money', value: 0, currency: rowTotal?.currency } as Money,
    };

    return {
        ...item,
        quantity: updatedQuantity,
        prices: updatedPrices,
    };
};

export function getCartItemVariation(item: ConfigurableCartItem): ConfigurableVariant | undefined {
    // get the value array of selected configurable options and filter null or undefined value
    const selectedConfigurableOptionValue = item.configurable_options
        .map(option => option?.value_id)
        .filter(value => !!value);
    const configurableProduct = item.product as ConfigurableProduct;
    if (!configurableProduct?.variants) {
        return undefined;
    }
    for (const variant of configurableProduct.variants) {
        if (!variant) {
            continue;
        }

        let matchOptionValueCount = 0;
        variant?.attributes?.forEach(attribute => {
            if (selectedConfigurableOptionValue.includes(attribute?.value_index ?? 0)) {
                matchOptionValueCount++;
            }
        });

        if (matchOptionValueCount === selectedConfigurableOptionValue.length) {
            return variant;
        }
    }
    return undefined;
}

export function isConfigurableCartItemExisting(item: ConfigurableCartItem, cartItem: CartItemInterface) {
    const matchVariation = getCartItemVariation(item);
    const isMatchVariation = matchVariation?.product?.id === cartItem.product.id;
    const isMatchCartItemId = item.id === cartItem.id;
    return isMatchVariation || isMatchCartItemId;
}

export function addItemWithQuantity(items: CartItem[], item: CartItemInterface, quantity: number) {
    if (quantity <= 0) throw new Error("cartQuantity can't be zero or less than zero");
    const existingItemIndex = items.findIndex(currentItem => {
        if (
            currentItem.__typename === CartItemTypeEnum.CONFIGURABLE ||
            currentItem.__typename === CartItemTypeEnum.CONFIGURABLE_LOCAL
        ) {
            return isConfigurableCartItemExisting(currentItem as ConfigurableCartItem, item);
        }
        return currentItem.product.id === item.product.id;
    });

    if (existingItemIndex > -1) {
        const newItems = [...items];
        const currentItem = newItems[existingItemIndex];
        const newQuantity = currentItem.quantity + quantity;
        newItems[existingItemIndex] = updateCartItemQuantityAndPrices(currentItem, newQuantity);
        return newItems;
    }
    const newCartItem = updateCartItemQuantityAndPrices({ ...item }, quantity);
    return [...items, newCartItem];
}

export function removeItemOrQuantity(items: CartItemInterface[], id: CartItemInterface['id'], quantity: number) {
    return items.reduce((acc: CartItemInterface[], item) => {
        if (item.id === id) {
            const newQuantity = item.quantity - quantity;
            const updatedCartItem = updateCartItemQuantityAndPrices(item, newQuantity);
            return newQuantity > 0 ? [...acc, { ...updatedCartItem }] : [...acc];
        }
        return [...acc, item];
    }, []);
}

export function updateItemWithQuantity(items: CartItemInterface[], id: CartItemInterface['id'], quantity: number) {
    const existingItemIndex = items.findIndex(item => item.id === id);
    if (existingItemIndex === -1) {
        throw new Error('Cart item does not exist');
    }
    const newItems = [...items];
    const currentItem = newItems[existingItemIndex];
    newItems[existingItemIndex] = updateCartItemQuantityAndPrices(currentItem, quantity);
    return newItems;
}

// Simple CRUD for Item
export function addItem(items: CartItemInterface[], item: CartItemInterface) {
    return [...items, item];
}

export function getItem(items: CartItemInterface[], id: CartItemInterface['id']) {
    if (!items) return undefined;
    return items.find(item => item?.id === id);
}

export function updateItem(items: CartItemInterface[], id: CartItemInterface['id'], item: UpdateItemInput) {
    return items.map(existingItem => (existingItem.id === id ? { ...existingItem, ...item } : existingItem));
}

export function removeItem(items: CartItemInterface[], id: CartItemInterface['id']) {
    return items.filter(existingItem => existingItem.id !== id);
}

export function inStock(items: CartItemInterface[], id: CartItemInterface['id']) {
    const item = getItem(items, id);
    if (item) return item.quantity < 2; // @TODO: should fetch from backend
    return false;
}

export const calculateItemTotals = (items: CartItemInterface[]) =>
    items.map(item => ({
        ...item,
        itemTotal: item.prices?.row_total?.value || 0,
    }));

export const calculateTotal = (items: CartItemInterface[]) =>
    items.reduce((total, item) => total + (item.prices?.row_total?.value || 0), 0);

export const calculateTotalItems = (items: CartItemInterface[]) => items?.reduce((sum, item) => sum + item.quantity, 0);

export const calculateUniqueItems = (items: CartItemInterface[]) => items.length;

interface PriceValues {
    totalAmount: number;
    tax: number;
    shipping_charge: number;
}

export const calculatePaidTotal = ({ totalAmount, tax, shipping_charge }: PriceValues, discount?: number) => {
    let paidTotal = totalAmount + tax + shipping_charge;
    if (discount) {
        paidTotal = paidTotal - discount;
    }
    return paidTotal;
};

export function getItemByProduct(items: CartItem[], cartItem: CartItemInterface) {
    if (!items) return undefined;

    return items.find(item => {
        if (
            item.__typename === CartItemTypeEnum.CONFIGURABLE ||
            item.__typename === CartItemTypeEnum.CONFIGURABLE_LOCAL
        ) {
            const isNewCartItem = item.id.startsWith('new-');
            const isExisting = isConfigurableCartItemExisting(item as ConfigurableCartItem, cartItem);
            return isExisting || isNewCartItem;
        }
        return item?.product.id === cartItem.product.id;
    });
}

export function groupCartItemsByProductId(cartItems: CartItemInterface[]): Record<string, CartItemInterface[]> {
    return cartItems.reduce<Record<string, CartItemInterface[]>>((acc, item) => {
        if (!item?.product?.id) return acc;
        const key = String(item.product.id);
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(item);
        return acc;
    }, {});
}

// ============================================================================
// CART PRICES CALCULATION - Local computation from cart items
// ============================================================================

/**
 * Calculate subtotal excluding tax from cart items
 * Derives from unitPrice × qty instead of trusting stored row_total.
 */
export const calculateSubtotalExcludingTax = (items: CartItemInterface[]): number => {
    return items.reduce((total, item) => {
        const rowTotal = getItemEffectiveUnitPrice(item) * (item.quantity ?? 0);
        return total + rowTotal;
    }, 0);
};

/**
 * Calculate subtotal including tax from cart items.
 * Uses unit-price derivation with server row_total_including_tax validation.
 *
 * NOTE (Vietnam market): VN product prices already include VAT, so in practice
 * this returns the same value as calculateSubtotalExcludingTax.
 */
export const calculateSubtotalIncludingTax = (items: CartItemInterface[]): number => {
    return items.reduce((total, item) => {
        const qty = item.quantity ?? 0;
        const serverRowTotalIncTax = item.prices?.row_total_including_tax?.value;
        if (serverRowTotalIncTax != null && serverRowTotalIncTax > 0) {
            const unitPrice = getItemEffectiveUnitPrice(item);
            const expectedTotal = unitPrice * qty;
            const tolerance = Math.max(expectedTotal * 0.005, 1);
            if (Math.abs(serverRowTotalIncTax - expectedTotal) <= tolerance) {
                return total + serverRowTotalIncTax;
            }
        }
        const rowTotalWithTax = getItemEffectiveUnitPrice(item) * qty;
        return total + rowTotalWithTax;
    }, 0);
};

/**
 * Calculate total item discounts from cart items
 * Sum of all items' total_item_discount
 */
export const calculateTotalItemDiscounts = (items: CartItemInterface[]): number => {
    return items.reduce((total, item) => {
        const itemDiscount = item.prices?.total_item_discount?.value ?? 0;
        return total + itemDiscount;
    }, 0);
};

/**
 * Collect all discounts from cart items
 * Aggregates discount information from all items
 */
export const collectItemDiscounts = (items: CartItemInterface[]): Discount[] => {
    const discountMap = new Map<string, Discount>();

    items.forEach(item => {
        const itemDiscounts = item.prices?.discounts ?? [];
        itemDiscounts.forEach(discount => {
            if (!discount) return;

            const key = discount.label;
            const existingDiscount = discountMap.get(key);

            if (existingDiscount) {
                // Aggregate discount amount
                const currentAmount = existingDiscount.amount?.value ?? 0;
                const newAmount = discount.amount?.value ?? 0;
                discountMap.set(key, {
                    ...existingDiscount,
                    amount: {
                        ...existingDiscount.amount,
                        value: currentAmount + newAmount,
                    },
                });
            } else {
                discountMap.set(key, discount);
            }
        });
    });

    return Array.from(discountMap.values());
};

/**
 * Calculate subtotal with discount excluding tax
 * subtotal_excluding_tax - total_item_discount
 */
export const calculateSubtotalWithDiscountExcludingTax = (items: CartItemInterface[]): number => {
    const subtotal = calculateSubtotalExcludingTax(items);
    const totalDiscount = calculateTotalItemDiscounts(items);
    return subtotal - totalDiscount;
};

/**
 * Create Money object from value
 */
const createMoneyObject = (value: number, currency: CurrencyEnum = 'VND' as CurrencyEnum): Money => ({
    __typename: 'Money',
    value,
    currency,
});

/**
 * Calculate complete cart prices from cart items
 * This replicates the CartPrices structure from Magento GraphQL
 *
 * @param items - Array of cart items
 * @param currency - Currency code (default: VND)
 * @param cartLevelDiscounts - Optional cart-level discounts (coupons, promotions)
 * @returns CartPrices object matching Magento structure
 */
export const calculateCartPricesFromItems = (
    items: CartItemInterface[],
    currency: CurrencyEnum = 'VND' as CurrencyEnum,
    cartLevelDiscounts?: Maybe<Array<Maybe<Discount>>>
): CartPrices => {
    // Calculate base values
    const subtotalExcludingTax = calculateSubtotalExcludingTax(items);
    const subtotalIncludingTax = calculateSubtotalIncludingTax(items);
    const subtotalWithDiscountExcludingTax = calculateSubtotalWithDiscountExcludingTax(items);

    // Collect all discounts (item-level + cart-level)
    const itemDiscounts = collectItemDiscounts(items);
    const allDiscounts = cartLevelDiscounts ? [...itemDiscounts, ...cartLevelDiscounts] : itemDiscounts;

    // Calculate total discount amount
    const totalDiscountAmount = allDiscounts.reduce((total, discount) => {
        return total + (discount?.amount?.value ?? 0);
    }, 0);

    // Calculate grand total (subtotal with tax - discounts)
    // Note: In Magento, grand_total = subtotal_including_tax - discounts + shipping + tax adjustments
    // For local calculation without shipping, we use: subtotal_including_tax - discounts
    const grandTotal = subtotalIncludingTax - totalDiscountAmount;

    return {
        __typename: 'CartPrices',
        subtotal_excluding_tax: createMoneyObject(subtotalExcludingTax, currency),
        subtotal_including_tax: createMoneyObject(subtotalIncludingTax, currency),
        subtotal_with_discount_excluding_tax: createMoneyObject(subtotalWithDiscountExcludingTax, currency),
        grand_total: createMoneyObject(grandTotal, currency),
        discounts: allDiscounts.length > 0 ? allDiscounts : null,
        applied_taxes: null, // Tax calculation requires server-side logic
    };
};

/**
 * Calculate cart prices for selected items only
 * Useful when calculating totals for checked items in cart
 *
 * @param items - Array of cart items
 * @param selectedItemIds - Array of selected item IDs
 * @param currency - Currency code
 * @param cartLevelDiscounts - Optional cart-level discounts
 * @returns CartPrices for selected items only
 */
export const calculateSelectedItemsPrices = (
    items: CartItemInterface[],
    selectedItemIds: string[],
    currency: CurrencyEnum = 'VND' as CurrencyEnum,
    cartLevelDiscounts?: Maybe<Array<Maybe<Discount>>>
): CartPrices => {
    const selectedItems = items.filter(item => selectedItemIds.includes(item.id));
    return calculateCartPricesFromItems(selectedItems, currency, cartLevelDiscounts);
};

// ============================================================================
// CART ITEM REFERENCE PRICES CALCULATION
// ============================================================================

/**
 * Calculate cart prices from selected cart item references
 * IMPORTANT: This function calculates prices ONLY for selected cart item references
 *
 * Key differences from calculateSelectedItemsPrices:
 * - Works with CartItemReference[] instead of CartItemInterface[]
 * - Filters by isSelected flag instead of selectedItemIds array
 * - Extracts CartItemInterface from cartItem within each reference
 * - Only includes item-level discounts (cart-level discounts handled by server)
 *
 * @param references - Array of cart item references
 * @param currency - Currency code (default: VND)
 * @returns CartPrices for selected references only
 */
export const calculateSelectedReferencesPrices = (
    references: CartItemReference[],
    currency: CurrencyEnum = 'VND' as CurrencyEnum
): CartPrices => {
    // Filter only selected references
    const selectedReferences = references.filter(ref => ref.isSelected === true);

    if (selectedReferences.length === 0) {
        return {
            __typename: 'CartPrices',
            subtotal_excluding_tax: createMoneyObject(0, currency),
            subtotal_including_tax: createMoneyObject(0, currency),
            subtotal_with_discount_excluding_tax: createMoneyObject(0, currency),
            grand_total: createMoneyObject(0, currency),
            discounts: null,
            applied_taxes: null,
        };
    }

    // Extract cart items from selected references.
    // CRITICAL: Normalize quantity from ref.qty (local truth) and strip stale
    // server discount data. After reconciliation, cartItem may carry pre-collectTotals
    // prices where discounts were computed for a different qty. Keeping them would
    // produce wildly wrong grandTotal.
    // Local grandTotal = subtotal (no discounts). Accurate discounts come from
    // the server via FastCartTotals.
    const cartItems: CartItemInterface[] = [];

    selectedReferences.forEach(ref => {
        if (ref.cartItem) {
            cartItems.push({
                ...(ref.cartItem as CartItemInterface),
                quantity: ref.qty, // Use ref.qty as source of truth
                prices: {
                    ...((ref.cartItem as CartItemInterface).prices as CartItemPrices),
                    discounts: null, // Strip stale discount data
                    total_item_discount: { __typename: 'Money', value: 0, currency: 'VND' as CurrencyEnum },
                },
            });
        }
    });

    if (cartItems.length === 0) {
        console.warn('[calculateSelectedReferencesPrices] No cart items found in selected references');
        return {
            __typename: 'CartPrices',
            subtotal_excluding_tax: createMoneyObject(0, currency),
            subtotal_including_tax: createMoneyObject(0, currency),
            subtotal_with_discount_excluding_tax: createMoneyObject(0, currency),
            grand_total: createMoneyObject(0, currency),
            discounts: null,
            applied_taxes: null,
        };
    }

    // Calculate prices from cart items (item-level discounts only)
    // Cart-level discounts are excluded (will be handled by server)
    return calculateCartPricesFromItems(cartItems, currency, undefined);
};

export const getProductId = (product: AddToCartProps['product'], variation: AddToCartProps['variation']) => {
    if (product.__typename === 'ConfigurableProduct') {
        return variation?.product?.id;
    }
    return product?.id;
};

export const getCartItem = (
    productId: number | undefined,
    itemByProductIdWithoutGiftsInCart$: ItemByProductIdWithoutGiftsInCart$
) => {
    if (!productId) return undefined;
    // Use peek() to get the raw function without Legend-State proxy wrapping.
    // .get() on an Observable<Function> may return a reactive proxy (Object)
    // instead of the callable function, causing "is not a function" errors.
    const lookup = itemByProductIdWithoutGiftsInCart$.peek() ?? itemByProductIdWithoutGiftsInCart$.get();
    return typeof lookup === 'function' ? lookup(productId) : undefined;
};
